import {
  type Point2,
  type Segment2,
  closestPointOnLine,
  distance,
  isParallel,
  isSamePoint,
  snapToOctant,
} from '@frontend/viewer-core';

import type {
  MeasureModifiers,
  MeasureTarget,
  Measurement,
  MeasurementKind,
} from './types';

/**
 * How far apart two lines' directions may be and still count as parallel.
 *
 * A tenth of a degree. DXF coordinates are often rounded, and on a short line
 * that rounding alone tilts it by more than 1e-4 rad, so a tighter tolerance
 * would let lines drawn parallel fall back to point to point.
 */
const PARALLEL_TOLERANCE_RAD = (0.1 * Math.PI) / 180;
const SIN_PARALLEL_TOLERANCE = Math.sin(PARALLEL_TOLERANCE_RAD);

type MeasureOptions = Partial<MeasureModifiers> & {
  /**
   * Distance within which two points are the same point: concentric centres,
   * or a perpendicular too short to mean anything. See
   * `DrawingModel.samePointTolerance`.
   */
  readonly tolerance: number;
};

/**
 * The same line or circle, clicked twice.
 *
 * The key alone is not enough: a MINSERT grid repeats one block entity under a
 * single key, and each copy is a different line. A circle is judged by its
 * geometry alone, because a hole drawn as arcs is one circle under several keys.
 */
const isSameTarget = (from: MeasureTarget, to: MeasureTarget): boolean => {
  if (from.kind === 'line' && to.kind === 'line') {
    return (
      from.entityKey === to.entityKey &&
      isSamePoint(from.segment[0], to.segment[0], 0) &&
      isSamePoint(from.segment[1], to.segment[1], 0)
    );
  }
  if (from.kind === 'circle' && to.kind === 'circle') {
    return isSamePoint(from.center, to.center, 0) && from.radius === to.radius;
  }
  return false;
};

/** Where a target measures from when the other side is not a line. */
const anchorOf = (target: MeasureTarget): Point2 =>
  target.kind === 'circle' ? target.center : target.spot;

/**
 * What a measurement from one target to another spans.
 *
 * A measurement starts from `from`. Perpendiculars drop only between parallel
 * lines and from a circle's centre to a line. Lines are infinite, so a
 * perpendicular may land past the end of the line it drops onto. A point or a
 * spot on a curve never drops one, which could land far outside the part.
 * Pairs with no caliper rule measure the straight distance between the two
 * click spots, as do a perpendicular that would be zero and two circles with
 * the same centre.
 *
 * The modifier keys take the caliper rules off. Both measure point to point
 * from where `from` measures from, which is its centre for a circle and its
 * click spot otherwise. A free point ends at `to`'s spot; the ortho lock moves
 * the end onto the nearest 45° from the start.
 */
export const measureBetween = (
  from: MeasureTarget,
  to: MeasureTarget,
  { tolerance, isOrthoLocked = false, isFreePoint = false }: MeasureOptions,
): Measurement => {
  const span = (kind: MeasurementKind, start: Point2, end: Point2) => ({
    kind,
    from,
    to,
    start,
    end,
    distance: distance(start, end),
  });
  const betweenSpots = () => span('pointToPoint', from.spot, to.spot);

  /** The foot of the perpendicular, or `null` when it would be zero. */
  const perpendicular = (anchor: Point2, segment: Segment2): Point2 | null => {
    const foot = closestPointOnLine(anchor, ...segment);
    return distance(anchor, foot) > tolerance ? foot : null;
  };

  if (isOrthoLocked) {
    const start = anchorOf(from);
    return span('orthoLocked', start, snapToOctant(start, anchorOf(to)));
  }
  if (isFreePoint) {
    return span('pointToPoint', anchorOf(from), to.spot);
  }

  if (isSameTarget(from, to)) {
    return span('sameTarget', from.spot, to.spot);
  }

  if (from.kind === 'line' && to.kind === 'line') {
    const foot = isParallel(from.segment, to.segment, SIN_PARALLEL_TOLERANCE)
      ? perpendicular(from.spot, to.segment)
      : null;
    return foot ? span('parallelGap', from.spot, foot) : betweenSpots();
  }

  if (from.kind === 'circle' && to.kind === 'circle') {
    return distance(from.center, to.center) > tolerance
      ? span('centerToCenter', from.center, to.center)
      : betweenSpots();
  }

  if (from.kind === 'circle' && to.kind === 'line') {
    const foot = perpendicular(from.center, to.segment);
    return foot ? span('centerToLine', from.center, foot) : betweenSpots();
  }

  if (from.kind === 'line' && to.kind === 'circle') {
    const foot = perpendicular(to.center, from.segment);
    return foot ? span('centerToLine', foot, to.center) : betweenSpots();
  }

  if (from.kind === 'circle' || to.kind === 'circle') {
    return span('pointToCenter', anchorOf(from), anchorOf(to));
  }

  return betweenSpots();
};
